import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { ArmSlot } from '../types/experiment.types';
import { armLabelKey, armSubtitleKey, hasSecondTestArm } from '../utils/arm-labels';

import { useExperiment } from './use-experiments';

export interface ArmLabels {
  /** Translated label for a data slot, e.g. `testA` → "Arm B". */
  label: (slot: ArmSlot) => string;
  /** Translated parenthetical subtitle for a data slot (Arm Comparison headers). */
  subtitle: (slot: ArmSlot) => string;
  /** True when Arm C runs — drives whether the `testB` column/series renders. */
  hasTestB: boolean;
}

/**
 * Resolves the display labels for an experiment's arms, so every dashboard on
 * the Performance tab names arms identically. Labels come from the fixed
 * slot→letter mapping in `utils/arm-labels.ts`; `armCount` is read from the
 * already-cached experiment query (no extra request) purely to decide whether
 * the second test arm renders.
 *
 * `label` / `subtitle` are memoised — consumers pass them as `useMemo` deps when
 * building chart series, so a fresh identity each render would defeat the memo.
 */
export function useArmLabels(experimentId: string): ArmLabels {
  const { t } = useTranslation('experiments');
  const { data: experiment } = useExperiment(experimentId);

  const label = useCallback((slot: ArmSlot) => t(armLabelKey(slot)), [t]);
  const subtitle = useCallback((slot: ArmSlot) => t(armSubtitleKey(slot)), [t]);

  return { label, subtitle, hasTestB: hasSecondTestArm(experiment?.armCount) };
}
